import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';
import {
    FaSpinner, FaHome, FaSearch, FaCheckCircle,
    FaFileInvoiceDollar, FaFilter, FaCalendarAlt, FaUserTie, FaUser
} from 'react-icons/fa';
import { ui } from '../utils/styles';

const ComissaoControlScreen = () => {
    const navigate = useNavigate();
    const URL_BACKEND = process.env.REACT_APP_URL_BACKEND;
    const authHeader = useMemo(() => ({ Authorization: `Bearer ${localStorage.getItem('userToken')}` }), []);

    // --- ESTADOS ---
    const [comissoes, setComissoes] = useState([]);
    const [loading, setLoading] = useState(false);

    // Estados para os filtros
    const [clientes, setClientes] = useState([]); // Para carregar o select de credores
    const [filtroStatus, setFiltroStatus] = useState('Aberto');
    const [filtroCliente, setFiltroCliente] = useState('');
    const [filtroDataI, setFiltroDataI] = useState('');
    const [filtroDataF, setFiltroDataF] = useState('');

    // --- CARREGAMENTO DE DADOS ---

    // Busca os clientes para o select
    useEffect(() => {
        const fetchClientes = async () => {
            try {
                const { data } = await axios.get(`${URL_BACKEND}/clientes`, { headers: authHeader });
                setClientes(data);
            } catch (e) { console.error("Erro ao carregar clientes"); }
        };
        fetchClientes();
    }, [URL_BACKEND, authHeader]);

    const fetchComissoes = async () => {
        setLoading(true);
        try {
            // Enviamos os filtros como params (Query String)
            const params = {
                st: filtroStatus,
                id_cliente: filtroCliente,
                datai: filtroDataI,
                dataf: filtroDataF
            };

            const { data } = await axios.get(`${URL_BACKEND}/comissoes`, {
                params,
                headers: authHeader
            });
            setComissoes(data);
        } catch (error) {
            toast.error('Erro ao carregar comissões.');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchComissoes();
    }, []); // Carrega ao iniciar

    // --- AÇÕES ---
    const handleBaixaComissao = async (id) => {
        if (!window.confirm("Confirmar recebimento desta comissão?")) return;
        try {
            await axios.put(`${URL_BACKEND}/comissao/baixa/${id}`, { status: 'Pago' }, { headers: authHeader });
            toast.success("Comissão marcada como paga!");
            fetchComissoes();
        } catch (error) {
            toast.error("Erro ao processar baixa.");
        }
    };

    const totalComissao = useMemo(() => {
        return comissoes.reduce((acc, curr) => acc + Number(curr.valor_comissao), 0);
    }, [comissoes]);

    const formatCurrency = (val) => Number(val).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

    return (
        <div style={ui.container}>
            {/* Header */}
            <div style={{ ...ui.headerGradient, background: 'linear-gradient(to right, #8b5cf6, #6d28d9)', display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '15px 20px', borderRadius: '12px', color: '#fff', marginBottom: '20px' }}>
                <h1 style={{ fontSize: '1.2rem' }}><FaFileInvoiceDollar /> Gestão Financeira de Comissões</h1>
                <button onClick={() => navigate('/dashboard')} style={ui.buttonOutlineWhite}><FaHome /> Painel</button>
            </div>

            {/* BARRA DE FILTROS */}
            <div style={{ ...ui.tableCard, padding: '20px', marginBottom: '20px' }}>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '15px', alignItems: 'flex-end' }}>

                    <div style={ui.formGroup}>
                        <label style={ui.label}><FaFilter /> Status</label>
                        <select style={ui.select} value={filtroStatus} onChange={e => setFiltroStatus(e.target.value)}>
                            <option value="Todas">Todos os Status</option>
                            <option value="Aberto">A Receber (Aberto)</option>
                            <option value="Pago">Recebido (Pago)</option>
                        </select>
                    </div>

                    <div style={ui.formGroup}>
                        <label style={ui.label}><FaUserTie /> Cliente (Credor)</label>
                        <select style={ui.select} value={filtroCliente} onChange={e => setFiltroCliente(e.target.value)}>
                            <option value="">Todos os Clientes</option>
                            {clientes.map(c => <option key={c.id} value={c.id}>{c.nome}</option>)}
                        </select>
                    </div>

                    <div style={ui.formGroup}>
                        <label style={ui.label}><FaCalendarAlt /> Início</label>
                        <input type="date" style={ui.input} value={filtroDataI} onChange={e => setFiltroDataI(e.target.value)} />
                    </div>

                    <div style={ui.formGroup}>
                        <label style={ui.label}><FaCalendarAlt /> Fim</label>
                        <input type="date" style={ui.input} value={filtroDataF} onChange={e => setFiltroDataF(e.target.value)} />
                    </div>

                    <button style={{ ...ui.buttonPrimary, height: '42px', background: '#8b5cf6' }} onClick={fetchComissoes}>
                        <FaSearch /> Filtrar
                    </button>
                </div>
            </div>

            {/* RESUMO RÁPIDO */}
            <div style={{ marginBottom: '20px', padding: '15px', background: '#ede9fe', borderRadius: '8px', display: 'inline-block' }}>
                <span style={{ color: '#5b21b6', fontWeight: 'bold' }}>Total Filtrado: </span>
                <span style={{ fontSize: '1.2rem', color: '#1e1b4b', fontWeight: '800' }}>{formatCurrency(totalComissao)}</span>
            </div>

            {/* TABELA DE RESULTADOS */}
            <div style={ui.tableCard}>
                {loading ? (
                    <div style={{ padding: '40px', textAlign: 'center' }}><FaSpinner className="fa-spin" size={30} /></div>
                ) : (
                    <div style={ui.tableScroll}>
                        <table style={ui.table}>
                            <thead style={ui.thead}>
                                <tr>
                                    <th style={ui.th}>Data</th>
                                    <th style={ui.th}>Devedor</th>
                                    <th style={ui.th}>Cliente</th>
                                    <th style={ui.th}>Vlr. Recebido</th>
                                    <th style={ui.th}>%</th>
                                    <th style={ui.th}>Minha Comissão</th>
                                    <th style={ui.th}>Status</th>
                                    <th style={ui.thActions}>Ações</th>
                                </tr>
                            </thead>
                            <tbody>
                                {comissoes.map((c) => (
                                    <tr key={c.id} style={ui.tr}>
                                        <td style={ui.td}>{new Date(c.data_lancamento).toLocaleDateString('pt-BR')}</td>
                                        <td style={ui.td}><FaUser size={12} color="#94a3b8" /> {c.nome_devedor}</td>
                                        <td style={ui.td}>{c.nome_cliente}</td>
                                        <td style={ui.td}>{formatCurrency(c.valor_original)}</td>
                                        <td style={ui.td}>{Number(c.porcentagem_cliente)}%</td>
                                        <td style={{ ...ui.td, fontWeight: 'bold', color: '#059669' }}>{formatCurrency(c.valor_comissao)}</td>
                                        <td style={ui.td}>
                                            <span style={{
                                                padding: '4px 8px', borderRadius: '12px', fontSize: '0.75rem',
                                                backgroundColor: c.status === 'Pago' ? '#dcfce7' : '#fee2e2',
                                                color: c.status === 'Pago' ? '#166534' : '#991b1b'
                                            }}>
                                                {c.status}
                                            </span>
                                        </td>
                                        <td style={ui.tdActions}>
                                            {c.status === 'Aberto' && (
                                                <button
                                                    onClick={() => handleBaixaComissao(c.id)}
                                                    style={{ ...ui.actionEdit, backgroundColor: '#10b981', color: '#fff' }}
                                                    title="Confirmar Recebimento"
                                                >
                                                    <FaCheckCircle />
                                                </button>
                                            )}
                                        </td>
                                    </tr>
                                ))}
                                {comissoes.length === 0 && (
                                    <tr><td colSpan="8" style={{ textAlign: 'center', padding: '20px', color: '#666' }}>Nenhuma comissão encontrada para os filtros aplicados.</td></tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        </div>
    );
};

export default ComissaoControlScreen;